import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, Loader2, MinusCircle, RefreshCw } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { EmptyState, ScoreBar } from "@/components/Primitives";

const STATUS_STYLE = {
  skipped: "text-muted-foreground",
  shortened: "text-[hsl(var(--warning))]",
  reinforced: "text-primary",
  included: "text-muted-foreground",
};

export default function Roadmap() {
  const [roadmap, setRoadmap] = useState(null);
  const [state, setState] = useState("loading");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/roadmap");
      setRoadmap(data);
      setState("ready");
    } catch (e) {
      if (e?.response?.status === 404) setState("empty");
      else {
        setError(errMsg(e, "Unable to load roadmap"));
        setState("error");
      }
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const generate = async () => {
    setState("loading");
    setError("");
    try {
      const { data } = await api.post("/roadmap/generate");
      setRoadmap(data);
      setState("ready");
    } catch (e) {
      setError(errMsg(e, "Unable to generate roadmap"));
      setState("empty");
    }
  };

  const toggle = async (slug, completed) => {
    await api.post(`/roadmap/lessons/${slug}/state`, { completed });
    load();
  };

  if (state === "loading")
    return <div className="grid place-items-center py-24" data-testid="roadmap-loading"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  if (state === "empty" || state === "error")
    return (
      <div className="space-y-6" data-testid="roadmap-empty">
        {error && <p className="text-sm text-destructive">{error}</p>}
        <EmptyState
          title="No roadmap generated yet"
          body="Your roadmap is built from your onboarding profile and your latest assessment scores. Complete both, then generate."
          action={
            <div className="flex gap-3">
              <Button className="rounded-none" onClick={generate} data-testid="roadmap-generate-btn">Generate roadmap</Button>
              <Link to="/assessment"><Button variant="outline" className="rounded-none">Take assessment</Button></Link>
            </div>
          }
        />
      </div>
    );

  return (
    <div className="space-y-8 fade-up" data-testid="roadmap-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="overline text-primary">my roadmap</p>
          <h1 className="mt-3 text-3xl sm:text-4xl font-display font-black tracking-tighter">
            {roadmap.estimatedWeeks} weeks to {roadmap.targetRole?.replace(/_/g, " ")}
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            {roadmap.completedLessons}/{roadmap.totalLessons} lessons done · {roadmap.weeklyStudyHours}h per week · based on assessment score {roadmap.basedOnAssessmentOverall}
          </p>
        </div>
        <Button variant="outline" className="rounded-none gap-2" onClick={generate} data-testid="roadmap-regenerate">
          <RefreshCw className="h-4 w-4" /> Regenerate
        </Button>
      </div>

      <div className="border border-border p-5">
        <div className="flex justify-between text-sm">
          <span className="overline text-muted-foreground">overall progress</span>
          <span className="font-mono">{roadmap.progressPercent}%</span>
        </div>
        <div className="mt-3"><ScoreBar value={roadmap.progressPercent} testId="roadmap-progress" /></div>
      </div>

      <div className="space-y-px">
        {roadmap.modules.map((m) => (
          <div key={m.slug} data-testid={`module-${m.slug}`} className={`border border-border p-6 ${m.status === "skipped" ? "opacity-60" : ""}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs text-primary">{String(m.order).padStart(2, "0")}</span>
                  <h2 className="font-display text-lg font-bold tracking-tight">{m.title}</h2>
                  <span className={`overline ${STATUS_STYLE[m.status]}`}>{m.status}</span>
                </div>
                <p className="mt-2 text-sm text-muted-foreground max-w-2xl">{m.summary}</p>
                <p className="mt-2 font-mono text-xs text-muted-foreground">{m.note}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-mono text-sm">{m.estimatedHours}h</p>
                <p className="text-xs text-muted-foreground">{m.startWeek ? `week ${m.startWeek}–${m.endWeek}` : "not scheduled"}</p>
              </div>
            </div>

            {m.status !== "skipped" && (
              <>
                <div className="mt-4"><ScoreBar value={m.progressPercent} /></div>
                <div className="mt-4 divide-y divide-border border-t border-border">
                  {m.lessons.map((l) => (
                    <div key={l.slug} className="py-3 flex items-center justify-between gap-4">
                      <button
                        data-testid={`toggle-${l.slug}`}
                        onClick={() => toggle(l.slug, !l.completed)}
                        className="flex items-center gap-3 text-left group"
                      >
                        {l.completed ? (
                          <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))] shrink-0" />
                        ) : (
                          <Circle className="h-4 w-4 text-muted-foreground shrink-0 group-hover:text-primary transition-colors duration-150" />
                        )}
                        <span className={`text-sm ${l.completed ? "text-muted-foreground line-through" : ""}`}>{l.title}</span>
                      </button>
                      <Link to={`/learn/${l.slug}`} data-testid={`open-${l.slug}`} className="font-mono text-xs text-primary hover:underline shrink-0">
                        open →
                      </Link>
                    </div>
                  ))}
                </div>
                {m.extraExercises > 0 && (
                  <p className="mt-3 font-mono text-xs text-primary flex items-center gap-2">
                    <MinusCircle className="h-3.5 w-3.5" /> +{m.extraExercises} extra exercises added for this gap
                  </p>
                )}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
